import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, Download, Wand2, Sparkles, FileText, Building2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";

const TIPI_AUTOCERT = [
  { v: "residenza", l: "Residenza" },
  { v: "stato_famiglia", l: "Stato di famiglia" },
  { v: "cittadinanza", l: "Cittadinanza italiana" },
  { v: "stato_civile", l: "Stato civile" },
  { v: "titolo_studio", l: "Titolo di studio" },
];

function OutputCard({ output, filename, tipo }) {
  const copy = () => {
    navigator.clipboard.writeText(output);
    toast.success("Copiato negli appunti");
  };
  const download = () => {
    const blob = new Blob([output], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Scaricato");
  };
  return (
    <Card className="p-6 rounded-2xl border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-xl">Anteprima</h3>
        {output && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={copy} className="rounded-full" data-testid={`${tipo}-copy`}>
              <Copy className="w-4 h-4 mr-1" /> Copia
            </Button>
            <Button size="sm" onClick={download} className="rounded-full" data-testid={`${tipo}-download`}>
              <Download className="w-4 h-4 mr-1" /> Scarica .txt
            </Button>
          </div>
        )}
      </div>
      {output ? (
        <pre className="text-xs whitespace-pre-wrap font-mono bg-accent/40 p-4 rounded-xl border max-h-[600px] overflow-auto" data-testid={`${tipo}-output`}>{output}</pre>
      ) : (
        <div className="text-muted-foreground text-sm py-16 text-center border border-dashed rounded-xl">
          Genera un documento per vedere l'anteprima qui.
        </div>
      )}
    </Card>
  );
}

/** Estrae nome/cognome dal full_name del profilo */
function splitFullName(full) {
  if (!full) return { nome: "", cognome: "" };
  const parts = full.trim().split(/\s+/);
  return { nome: parts[0] || "", cognome: parts.slice(1).join(" ") || "" };
}

/** Compone la stringa residenza dai campi profilo */
function buildResidenza(u) {
  if (!u) return "";
  const parts = [u.indirizzo, u.cap && u.citta ? `${u.cap} ${u.citta}` : (u.cap || u.citta), u.provincia ? `(${u.provincia})` : ""].filter(Boolean);
  return parts.join(", ");
}

// ============================================================
// TAB 1: AUTOCERTIFICAZIONE
// ============================================================
function AutocertTab() {
  const { user } = useAuth();
  const [tipo, setTipo] = useState("residenza");
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [luogo, setLuogo] = useState("");
  const [dataN, setDataN] = useState("");
  const [residenza, setResidenza] = useState("");
  const [specifici, setSpecifici] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  const prefill = () => {
    if (!user) return;
    const { nome: n, cognome: c } = splitFullName(user.full_name);
    setNome(n);
    setCognome(c);
    setLuogo(user.luogo_nascita || "");
    setDataN(user.data_nascita || "");
    setResidenza(buildResidenza(user));
    toast.success("Dati precompilati dal tuo profilo");
  };

  const generate = async () => {
    if (!nome || !cognome) { toast.error("Nome e cognome obbligatori"); return; }
    setLoading(true);
    try {
      const r = await api.post("/generate/autocertificazione", {
        tipo, nome, cognome, luogo_nascita: luogo, data_nascita: dataN, residenza, dati_specifici: specifici,
      });
      setOutput(r.data.documento);
      toast.success("Documento generato");
    } catch { toast.error("Errore generazione"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <div className="flex items-start justify-between gap-3 mb-4">
          <h3 className="font-heading font-semibold text-xl">Dati dichiarante</h3>
          <Button variant="outline" size="sm" onClick={prefill} className="rounded-full text-xs" data-testid="autocert-prefill">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Precompila da profilo
          </Button>
        </div>
        <div className="space-y-3">
          <div><Label>Tipo di dichiarazione</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="h-11 rounded-xl mt-1.5" data-testid="gen-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPI_AUTOCERT.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome</Label><Input value={nome} onChange={(e) => setNome(e.target.value)} className="h-11 rounded-xl mt-1.5" data-testid="gen-nome" /></div>
            <div><Label>Cognome</Label><Input value={cognome} onChange={(e) => setCognome(e.target.value)} className="h-11 rounded-xl mt-1.5" data-testid="gen-cognome" /></div>
          </div>
          <div><Label>Luogo di nascita</Label><Input value={luogo} onChange={(e) => setLuogo(e.target.value)} className="h-11 rounded-xl mt-1.5" data-testid="gen-luogo" /></div>
          <div><Label>Data di nascita</Label><Input type="date" value={dataN} onChange={(e) => setDataN(e.target.value)} className="h-11 rounded-xl mt-1.5" data-testid="gen-datan" /></div>
          <div><Label>Residenza</Label><Input value={residenza} onChange={(e) => setResidenza(e.target.value)} className="h-11 rounded-xl mt-1.5" placeholder="Via, città, CAP" data-testid="gen-res" /></div>
          <div><Label>Dettagli specifici</Label><Textarea value={specifici} onChange={(e) => setSpecifici(e.target.value)} className="rounded-xl mt-1.5" placeholder="Es. Via Roma 12, 20100 Milano" data-testid="gen-spec" /></div>
          <Button onClick={generate} disabled={loading} className="rounded-full w-full h-12" data-testid="gen-submit">
            <Wand2 className="w-4 h-4 mr-2" /> {loading ? "Genero..." : "Genera autocertificazione"}
          </Button>
        </div>
      </Card>
      <OutputCard output={output} filename={`autocertificazione_${tipo}.txt`} tipo="autocert" />
    </div>
  );
}

// ============================================================
// TAB 2: SCHEDA PROFILO (per bandi/allegati)
// ============================================================
function SchedaProfiloTab() {
  const { user } = useAuth();
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/generate/scheda-profilo");
      setOutput(r.data.documento);
      toast.success("Scheda profilo generata");
    } catch { toast.error("Errore generazione"); }
    finally { setLoading(false); }
  };

  // Compute filled ratio for user feedback
  const trackedFields = ["ragione_sociale", "codice_fiscale", "partita_iva", "codice_ateco", "settore", "indirizzo", "cap", "citta", "provincia", "regione", "telefono", "pec", "rea_numero", "rea_cciaa", "iban", "codice_sdi", "cassa_previdenziale"];
  const filled = user ? trackedFields.filter((f) => user[f]).length : 0;
  const total = trackedFields.length;
  const pct = Math.round((filled / total) * 100);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><Building2 className="w-5 h-5 text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold text-xl">Scheda profilo pronta all'uso</h3>
            <p className="text-xs text-muted-foreground">Riepilogo completo dei tuoi dati aziendali/professionali da allegare a bandi e domande.</p>
          </div>
        </div>

        <div className="rounded-xl bg-accent/40 border p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">Profilo compilato al {pct}%</span>
            <span className="text-xs text-muted-foreground font-mono">{filled}/{total} campi</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
          {pct < 80 && (
            <p className="text-xs text-muted-foreground mt-2">
              Suggerimento: completa il profilo in <Link to="/app/impostazioni" className="text-primary font-semibold underline">Impostazioni</Link> per avere una scheda senza placeholder da compilare a mano.
            </p>
          )}
        </div>

        <ul className="text-sm text-muted-foreground space-y-1.5 mb-6 list-disc list-inside">
          <li>Anagrafica + fisco (P.IVA, ATECO, regime)</li>
          <li>Sede legale + contatti (telefono, PEC, sito)</li>
          <li>REA, CCIAA, albo professionale</li>
          <li>Cassa previdenziale, INPS, INAIL</li>
          <li>IBAN, banca, codice SDI</li>
        </ul>

        <Button onClick={generate} disabled={loading} className="rounded-full w-full h-12" data-testid="scheda-generate">
          <FileText className="w-4 h-4 mr-2" /> {loading ? "Genero..." : "Genera scheda profilo"}
        </Button>
      </Card>
      <OutputCard output={output} filename="scheda_profilo_tuotax.txt" tipo="scheda" />
    </div>
  );
}

// ============================================================
// TAB 3: DOMANDA BANDO
// ============================================================
function DomandaBandoTab({ prefillTitolo = "", prefillEnte = "" }) {
  const [bandoTitolo, setBandoTitolo] = useState(prefillTitolo);
  const [enteBando, setEnteBando] = useState(prefillEnte);
  const [importo, setImporto] = useState("");
  const [motivazione, setMotivazione] = useState("");
  const [obiettivi, setObiettivi] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  // Aggiorna se cambiano i prefill (utente naviga da un altro bando)
  useEffect(() => { if (prefillTitolo) setBandoTitolo(prefillTitolo); }, [prefillTitolo]);
  useEffect(() => { if (prefillEnte) setEnteBando(prefillEnte); }, [prefillEnte]);

  const generate = async () => {
    if (!bandoTitolo.trim()) { toast.error("Titolo del bando obbligatorio"); return; }
    setLoading(true);
    try {
      const r = await api.post("/generate/domanda-bando", {
        bando_titolo: bandoTitolo,
        ente_bando: enteBando,
        importo_richiesto: importo ? parseFloat(importo) : null,
        motivazione,
        obiettivi,
      });
      setOutput(r.data.documento);
      toast.success("Modello domanda generato");
    } catch { toast.error("Errore generazione"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><Send className="w-5 h-5 text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold text-xl">Modello di domanda per bando</h3>
            <p className="text-xs text-muted-foreground">Lettera pronta con i tuoi dati già inseriti — copia, adatta all'ente, firma.</p>
          </div>
        </div>
        <div className="space-y-3">
          <div><Label>Titolo del bando *</Label>
            <Input value={bandoTitolo} onChange={(e) => setBandoTitolo(e.target.value)} placeholder="Es. Bando Regione Lombardia Innovazione 2026"
              className="h-11 rounded-xl mt-1.5" data-testid="domanda-titolo" />
          </div>
          <div><Label>Ente erogatore</Label>
            <Input value={enteBando} onChange={(e) => setEnteBando(e.target.value)} placeholder="Es. Regione Lombardia, Camera di Commercio Milano..."
              className="h-11 rounded-xl mt-1.5" data-testid="domanda-ente" />
          </div>
          <div><Label>Importo richiesto (€)</Label>
            <Input type="number" value={importo} onChange={(e) => setImporto(e.target.value)} placeholder="Es. 15000"
              className="h-11 rounded-xl mt-1.5" data-testid="domanda-importo" />
          </div>
          <div><Label>Motivazione</Label>
            <Textarea value={motivazione} onChange={(e) => setMotivazione(e.target.value)} rows={3}
              placeholder="Perché partecipi al bando e come userai le risorse..."
              className="rounded-xl mt-1.5" data-testid="domanda-motivazione" />
          </div>
          <div><Label>Obiettivi del progetto</Label>
            <Textarea value={obiettivi} onChange={(e) => setObiettivi(e.target.value)} rows={3}
              placeholder="Elenca 2-3 obiettivi principali..."
              className="rounded-xl mt-1.5" data-testid="domanda-obiettivi" />
          </div>
          <Button onClick={generate} disabled={loading} className="rounded-full w-full h-12" data-testid="domanda-generate">
            <Wand2 className="w-4 h-4 mr-2" /> {loading ? "Genero..." : "Genera domanda"}
          </Button>
        </div>
      </Card>
      <OutputCard output={output} filename={`domanda_${bandoTitolo.replace(/[^a-z0-9]+/gi, "_").toLowerCase().slice(0, 40) || "bando"}.txt`} tipo="domanda" />
    </div>
  );
}

// ============================================================
// MAIN
// ============================================================
export default function Generator() {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const titoloFromUrl = searchParams.get("titolo") || "";
  const enteFromUrl = searchParams.get("ente") || "";
  const defaultTab = tabFromUrl === "domanda" || tabFromUrl === "autocert" || tabFromUrl === "scheda" ? tabFromUrl : "scheda";

  // Se arriva da un bando, forzo attivazione tab domanda
  const [activeTab, setActiveTab] = useState(defaultTab);
  useEffect(() => {
    if (titoloFromUrl || enteFromUrl) setActiveTab("domanda");
  }, [titoloFromUrl, enteFromUrl]);

  return (
    <div className="space-y-6" data-testid="generator-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Generatore documenti</div>
        <h1 className="font-heading font-bold text-4xl tracking-tighter">Documenti pronti, dati precompilati.</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Autocertificazioni, schede profilo e modelli di domanda per bandi — TUOTAX riempie tutto con i dati del tuo profilo.
          Tu controlli, adatti se serve, e firmi.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 h-auto rounded-2xl bg-accent p-1.5 mb-4">
          <TabsTrigger value="scheda" data-testid="tab-scheda" className="rounded-xl py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <Building2 className="w-4 h-4 mr-2 hidden sm:inline" /> Scheda profilo
          </TabsTrigger>
          <TabsTrigger value="domanda" data-testid="tab-domanda" className="rounded-xl py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <Send className="w-4 h-4 mr-2 hidden sm:inline" /> Domanda bando
          </TabsTrigger>
          <TabsTrigger value="autocert" data-testid="tab-autocert" className="rounded-xl py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <FileText className="w-4 h-4 mr-2 hidden sm:inline" /> Autocertificazione
          </TabsTrigger>
        </TabsList>

        <TabsContent value="scheda"><SchedaProfiloTab /></TabsContent>
        <TabsContent value="domanda"><DomandaBandoTab prefillTitolo={titoloFromUrl} prefillEnte={enteFromUrl} /></TabsContent>
        <TabsContent value="autocert"><AutocertTab /></TabsContent>
      </Tabs>
    </div>
  );
}
